import { Table as CoreTable, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { IconButtonRegular as IconButton } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonRegular';
import { IconButtonClean } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonClean';
import DoneIcon from '@components/icons/Done.svg';
import EditIcon from '@components/icons/Edit.svg';
import PencilSvg from '@components/icons/Pencil.svg';
import TrashBin from '@components/icons/TrashBin.svg';
import { PagePagination } from '@components/core-ui/Pagination';
import { TagInfoRounded } from '@components/core-ui/Tag/variations/TagInfoRounded';
import { TagSuccessRounded } from '@components/core-ui/Tag/variations/TagSuccessRounded';
import { routes } from '@routes';
import { Fragment } from 'react';

import { InlineEditRow } from './components/InlineEditRow';
import { StyledLink } from './styles/StyledLink';
import { TProps } from './types/TProps';

export const Table = ({
  organizations,
  page,
  onPageChange,
  pageSize,
  setPageSize,
  editId,
  setEditId,
  onOpenAIModelChoiceModal,
  onEditFeatureFlag,
  onRemoveFeatureFlag,
  canDeleteOrganizations,
  isDeletingOrganization,
  onDeleteOrganization,
}: TProps) => {
  return (
    <>
      <TableWrapper>
        <CoreTable>
          <Thead $sticky>
            <Tr>
              <Th>#</Th>
              <Th>Name</Th>
              <Th>URL</Th>
              <Th>Members</Th>
              <Th>Workspaces</Th>
              <Th>AI Config</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {organizations?.data?.map((data, index) => (
              <Fragment key={data.id}>
                <Tr>
                  <Td>{index + 1}</Td>
                  <Td>
                    <div className="flex items-center gap-2">
                      <StyledLink to={routes.adminOrganizationDetails.make(data.id)}>{data.name}</StyledLink>
                      {data.managementSource === 'EXTERNAL' ? (
                        <TagInfoRounded>Externally managed</TagInfoRounded>
                      ) : null}
                    </div>
                  </Td>
                  <Td>{data.slug}</Td>
                  <Td>
                    <StyledLink to={routes.adminOrganizationUsersList.make(data.id)}>
                      <div className="flex items-center gap-2">
                        <TagInfoRounded className="font-semibold">
                          {data.membersCount} {data.membersCount === 1 ? 'member' : 'members'}
                        </TagInfoRounded>
                      </div>
                    </StyledLink>
                  </Td>
                  <Td>
                    <div className="flex items-center gap-2">
                      <TagSuccessRounded className="font-semibold">
                        {data.workspacesCount} {data.workspacesCount === 1 ? 'workspace' : 'workspaces'}
                      </TagSuccessRounded>
                    </div>
                  </Td>
                  <Td>
                    <IconButtonClean
                      type="button"
                      title="ASR and LLM config"
                      className="ml-[10px]"
                      onClick={(e) => onOpenAIModelChoiceModal(e, data)}
                    >
                      <PencilSvg />
                    </IconButtonClean>
                  </Td>
                  <Td>
                    <div className="flex items-center gap-2">
                      {data.id !== editId ? (
                        <IconButton title="Edit" onClick={() => setEditId(data.id)}>
                          <EditIcon />
                        </IconButton>
                      ) : (
                        <IconButton title="Done" onClick={() => setEditId('')}>
                          <DoneIcon width={18} height={14} />
                        </IconButton>
                      )}
                      {canDeleteOrganizations ? (
                        <IconButton
                          title="Delete organization"
                          disabled={isDeletingOrganization}
                          onClick={() => onDeleteOrganization(data)}
                        >
                          <TrashBin />
                        </IconButton>
                      ) : null}
                    </div>
                  </Td>
                </Tr>
                <InlineEditRow
                  data={data}
                  featureFlags={organizations.featureFlags}
                  editId={editId}
                  onEditFeatureFlag={onEditFeatureFlag}
                  onRemoveFeatureFlag={onRemoveFeatureFlag}
                />
              </Fragment>
            ))}
          </Tbody>
        </CoreTable>
      </TableWrapper>
      <PagePagination
        totalItemsCount={organizations.count ?? 0}
        page={page}
        onPageChange={onPageChange}
        pageSize={pageSize}
        setPageSize={setPageSize}
      />
    </>
  );
};
